import { twJoin } from 'tailwind-merge';

import type {
  AnnouncementBannerProps,
  AnnouncementPresentation,
} from '@/components/announcementBanner/types';
import {
  DARK_VARIANT_TEXT_COLOR,
  TONE_STYLES,
  parseAlertVariant,
} from '@/components/announcementBanner/variants';
import { Alert, AlertIcon, AlertText } from '@/components/ui/alert';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';

type AnnouncementBannerBodyProps = Omit<AnnouncementBannerProps, 'isEnabled'> & {
  presentation: AnnouncementPresentation;
  onCtaPress: () => void;
};

/**
 * The banner's contents, shared by both platforms.
 *
 * This owns the structure — leading icon, badge, copy, call to action — and
 * nothing about how either platform sizes or frames it. Those come in through
 * `presentation`, and the surrounding shell is the platform file's job: web
 * floats this in a card, native renders it full-bleed in a screen header.
 */
export function AnnouncementBannerBody({
  presentation,
  onCtaPress,
  variant = 'success',
  badge,
  title,
  message,
  showIcon = true,
  emoji,
  ctaLabel,
  ctaUrl,
  ctaAction,
  onPress,
  buttonClassName,
  textColor,
}: AnnouncementBannerBodyProps) {
  const { tone, isDark } = parseAlertVariant(variant);

  const Icon = TONE_STYLES[tone].icon;

  const effectiveTextColor = textColor || (isDark ? DARK_VARIANT_TEXT_COLOR : '');

  const showCta = !!ctaLabel && (!!ctaUrl || !!onPress || !!ctaAction);

  return (
    <Alert testID="announcement-banner" action={tone} className={presentation.alertClassName}>
      <HStack className="min-w-0 flex-1 gap-2 web:w-full">
        {emoji || showIcon ? (
          <View className={presentation.leadingClassName}>
            {emoji ? (
              <Text aria-hidden className={presentation.emojiClassName}>
                {emoji}
              </Text>
            ) : (
              <AlertIcon as={Icon} className="mt-0.5 lg:mt-1" />
            )}
          </View>
        ) : null}

        {/* Fixed height equal to the first text line so the chip stays centered
            on that line whether or not the message wraps; self-start pins it to
            the first line rather than the center of a multi-line block. */}
        {badge ? (
          <View className={presentation.badgeClassName}>
            <Text size="2xs" className="font-semibold uppercase tracking-wide text-primary-700">
              {badge}
            </Text>
          </View>
        ) : null}

        <View className="min-w-0 flex-1">
          <AlertText
            size={presentation.messageSize}
            className={twJoin('break-words', effectiveTextColor || undefined)}>
            {title && (
              <AlertText
                size={presentation.titleSize}
                bold
                className={effectiveTextColor || undefined}>
                {title}{' '}
              </AlertText>
            )}
            {message}
          </AlertText>
        </View>
      </HStack>

      {showCta ? (
        <Button
          size={presentation.ctaSize}
          hitSlop={presentation.ctaHitSlop}
          className={twJoin(presentation.ctaClassName, buttonClassName)}
          onPress={onCtaPress}>
          <ButtonText className="font-normal">{ctaLabel}</ButtonText>
        </Button>
      ) : null}
    </Alert>
  );
}
